import { useState, useRef, useEffect } from "react"
import { Wand2, AtSign, Paperclip, Braces, ArrowUp, Loader2, FileText, Globe, X } from "lucide-react"
import { Button } from "@aix/ui"
import toast from "react-hot-toast"
import { PromptBuilderModal } from "./PromptBuilder"
import ModelSelector, { DEFAULT_MODEL_ID } from "./ModelSelector"
import StyleGuideBadge from "./StyleGuideBadge"
import type { StyleGuideSource } from "./StyleGuideBadge"

// ============================================================================
// TYPES
// ============================================================================

export type GenerationMode = "CREATE" | "EDIT" | "QUICKPATCH"

interface PageInfo {
  id: string
  name: string
  slug: string
  html_content?: string | null
}

interface AtReference {
  type: "page"
  id: string
  name: string
  content?: string
}

interface SavedStyleGuide {
  id: string
  name: string
}

export interface AuraPromptInputProps {
  /** Visual variant - 'hero' for main page, 'compact' for sidebar */
  variant?: "hero" | "compact"

  /** Current input value (controlled) */
  value: string
  /** Called when input changes */
  onChange: (value: string) => void
  /** Called when user submits */
  onSubmit: (options: {
    message: string
    modelId: string
    mode: GenerationMode
    styleReference?: { url?: string; imageUrl?: string }
    atReferences?: AtReference[]
  }) => void

  /** Whether currently generating/loading */
  isLoading?: boolean

  /** Placeholder text */
  placeholder?: string

  // Mode toggle props (only for compact variant)
  /** Show mode toggle (CREATE/QUICKPATCH) */
  showModeToggle?: boolean
  /** Current generation mode */
  generationMode?: GenerationMode
  /** Called when mode changes */
  onModeChange?: (mode: GenerationMode) => void

  // Context badge props (only for compact variant)
  /** Current page name to show in badge */
  currentPageName?: string
  /** Style guide source config */
  styleGuideSource?: StyleGuideSource
  /** Called when style guide source changes */
  onStyleGuideChange?: (source: StyleGuideSource) => void
  /** Called when extracting style from current page */
  onExtractStyleGuide?: () => void
  /** Saved style guides list */
  savedStyleGuides?: SavedStyleGuide[]

  // @ references props
  /** Available pages for @ references */
  pages?: PageInfo[]

  // Image upload handler (optional - for when parent needs to upload)
  /** Called to upload an image, should return the URL */
  onUploadImage?: (file: File) => Promise<string>
}

// ============================================================================
// MODE TOGGLE COMPONENT
// ============================================================================

interface ModeToggleProps {
  mode: GenerationMode
  onModeChange: (mode: GenerationMode) => void
  disabled?: boolean
}

const ModeToggle = ({ mode, onModeChange, disabled }: ModeToggleProps) => {
  return (
    <div className="flex items-center bg-gray-800/50 rounded-lg p-0.5">
      <button
        onClick={() => onModeChange("CREATE")}
        disabled={disabled}
        className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
          mode === "CREATE" ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        Create
      </button>
      <button
        onClick={() => onModeChange("QUICKPATCH")}
        disabled={disabled}
        className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
          mode === "QUICKPATCH" ? "bg-green-600 text-white" : "text-gray-400 hover:text-white"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        title="Fast JSON patches - ~90% fewer tokens"
      >
        Quick
      </button>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AuraPromptInput = ({
  variant = "hero",
  value,
  onChange,
  onSubmit,
  isLoading = false,
  placeholder,
  showModeToggle = false,
  generationMode = "CREATE",
  onModeChange,
  currentPageName,
  styleGuideSource,
  onStyleGuideChange,
  onExtractStyleGuide,
  savedStyleGuides = [],
  pages = [],
  onUploadImage,
}: AuraPromptInputProps) => {
  const [selectedModelId, setSelectedModelId] = useState<string>(DEFAULT_MODEL_ID)
  const [showModelDropdown, setShowModelDropdown] = useState(false)
  const [showPromptBuilder, setShowPromptBuilder] = useState(false)
  const [atReferences, setAtReferences] = useState<AtReference[]>([])

  // Autocomplete state
  const [showAtAutocomplete, setShowAtAutocomplete] = useState(false)
  const [atAutocompleteFilter, setAtAutocompleteFilter] = useState("")
  const [atAutocompleteIndex, setAtAutocompleteIndex] = useState(0)
  const [atAutocompleteStart, setAtAutocompleteStart] = useState<number | null>(null)

  // @ dropdown state
  const [showAtDropdown, setShowAtDropdown] = useState(false)

  // Style reference state (URLs and images)
  const [detectedUrls, setDetectedUrls] = useState<string[]>([])
  const [uploadedImages, setUploadedImages] = useState<{ file: File; preview: string }[]>([])
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const atDropdownRef = useRef<HTMLDivElement>(null)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  // Variant-specific styling
  const isHero = variant === "hero"
  const containerClass = isHero ? "rounded-2xl" : "rounded-xl"
  const textareaClass = isHero
    ? "w-full px-6 py-5 bg-transparent text-lg text-white placeholder-gray-500 resize-none focus:outline-hidden disabled:opacity-50"
    : "w-full px-4 py-3 pr-32 bg-transparent text-sm text-white placeholder-gray-500 resize-none focus:outline-hidden disabled:opacity-50 min-h-[60px]"
  const bottomBarClass = isHero
    ? "flex items-center justify-between px-4 py-3 border-t border-gray-800"
    : "flex items-center justify-between px-3 py-2 border-t border-gray-800"
  const buttonClass = isHero
    ? "flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-lg text-sm font-medium hover:bg-gray-700 transition"
    : "flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-800 rounded-lg text-xs font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition disabled:opacity-50"
  const iconButtonClass = isHero
    ? "p-2.5 bg-gray-800 rounded-lg hover:bg-gray-700 transition"
    : "p-2 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition disabled:opacity-50"
  const rows = isHero ? 3 : 2

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      const maxHeight = isHero ? 200 : 120
      const minHeight = isHero ? 120 : 60
      inputRef.current.style.height = Math.max(minHeight, Math.min(inputRef.current.scrollHeight, maxHeight)) + "px"
    }
  }, [value, isHero])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (atDropdownRef.current && !atDropdownRef.current.contains(event.target as Node)) {
        setShowAtDropdown(false)
      }
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowAtAutocomplete(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Filter pages for autocomplete
  const filteredPages = pages.filter(
    (page) =>
      page.name.toLowerCase().includes(atAutocompleteFilter.toLowerCase()) &&
      !atReferences.some((ref) => ref.id === page.id),
  )

  // Reset autocomplete index when filter changes
  useEffect(() => {
    setAtAutocompleteIndex(0)
  }, [atAutocompleteFilter])

  // URL detection regex
  const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi

  // Handle input change to detect @ autocomplete and URLs
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart || 0
    onChange(newValue)

    // Detect URLs in the input
    const urlMatches = newValue.match(URL_REGEX) || []
    const uniqueUrls = [...new Set(urlMatches)]
    setDetectedUrls(uniqueUrls)

    // Find if we're in an @ mention context (only if pages available)
    if (pages.length > 0) {
      const textBeforeCursor = newValue.slice(0, cursorPos)
      const atMatch = textBeforeCursor.match(/@(\w*)$/)

      if (atMatch) {
        setShowAtAutocomplete(true)
        setAtAutocompleteFilter(atMatch[1])
        setAtAutocompleteStart(cursorPos - atMatch[0].length)
      } else {
        setShowAtAutocomplete(false)
        setAtAutocompleteFilter("")
        setAtAutocompleteStart(null)
      }
    }
  }

  // Insert selected page from autocomplete
  const handleAutocompleteSelect = (page: PageInfo) => {
    if (atAutocompleteStart === null) return

    const beforeAt = value.slice(0, atAutocompleteStart)
    const afterCursor = value.slice(inputRef.current?.selectionStart || value.length)
    const newValue = `${beforeAt}@${page.name} ${afterCursor}`

    onChange(newValue)
    setShowAtAutocomplete(false)
    setAtAutocompleteFilter("")
    setAtAutocompleteStart(null)

    // Add to references
    const newRef: AtReference = {
      type: "page",
      id: page.id,
      name: page.name,
      content: page.html_content || undefined,
    }
    setAtReferences((prev) => [...prev, newRef])

    // Focus back on input
    setTimeout(() => {
      inputRef.current?.focus()
      const newCursorPos = beforeAt.length + page.name.length + 2
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  // Handle file selection (from file picker or drag/drop)
  const handleImageFiles = (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((file) =>
      ["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type),
    )

    if (imageFiles.length === 0) {
      toast.error("Please select an image file (PNG, JPG, WebP, or GIF)")
      return
    }

    // Create preview URLs and add to state
    const newImages = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }))

    setUploadedImages((prev) => [...prev, ...newImages])
  }

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  // Handle drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (e.dataTransfer.files?.length > 0) {
      void handleImageFiles(e.dataTransfer.files)
    }
  }

  // Handle paste for images
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return

    const imageItems = Array.from(items).filter((item) => item.type.startsWith("image/"))
    if (imageItems.length > 0) {
      e.preventDefault()
      const files = imageItems.map((item) => item.getAsFile()).filter(Boolean)
      void handleImageFiles(files)
    }
  }

  // Remove uploaded image
  const removeUploadedImage = (index: number) => {
    setUploadedImages((prev) => {
      const newImages = [...prev]
      URL.revokeObjectURL(newImages[index].preview)
      newImages.splice(index, 1)
      return newImages
    })
  }

  // Remove detected URL
  const removeDetectedUrl = (url: string) => {
    onChange(value.replace(url, "").trim())
    setDetectedUrls((prev) => prev.filter((u) => u !== url))
  }

  // Handle file picker button click
  const handleFilePickerClick = () => {
    fileInputRef.current?.click()
  }

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      void handleImageFiles(e.target.files)
    }
    e.target.value = ""
  }

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = (error) => reject(error)
    })
  }

  const handleSubmit = async () => {
    if (!value.trim() || isLoading) return

    const message = value.trim()

    // Prepare style reference if we have URLs or images
    let styleReference: { url?: string; imageUrl?: string } | undefined

    // If we have detected URLs, use the first one as style reference
    if (detectedUrls.length > 0) {
      styleReference = { url: detectedUrls[0] }
    }

    // If we have uploaded images and onUploadImage is provided, upload and use
    if (uploadedImages.length > 0 && !styleReference && onUploadImage) {
      setIsUploadingImage(true)
      try {
        const imageFile = uploadedImages[0].file
        const imageUrl = await onUploadImage(imageFile)
        styleReference = { imageUrl }
      } catch (error: any) {
        toast.error("Failed to upload image: " + error.message)
        setIsUploadingImage(false)
        return
      }
      setIsUploadingImage(false)
    }

    // Clear state
    onChange("")
    setDetectedUrls([])
    setUploadedImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview))
      return []
    })

    // Call onSubmit
    onSubmit({
      message,
      modelId: selectedModelId,
      mode: generationMode,
      styleReference,
      atReferences: atReferences.length > 0 ? atReferences : undefined,
    })

    // Clear @ references after submit
    setAtReferences([])
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle autocomplete navigation
    if (showAtAutocomplete && filteredPages.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setAtAutocompleteIndex((prev) => (prev + 1) % filteredPages.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setAtAutocompleteIndex((prev) => (prev - 1 + filteredPages.length) % filteredPages.length)
        return
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault()
        handleAutocompleteSelect(filteredPages[atAutocompleteIndex])
        return
      }
      if (e.key === "Escape") {
        e.preventDefault()
        setShowAtAutocomplete(false)
        return
      }
    }

    // Submit handling
    if (isHero) {
      // Hero: Cmd/Ctrl+Enter to submit
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        void handleSubmit()
      }
    } else {
      // Compact: Enter to submit (Shift+Enter for newline)
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        void handleSubmit()
      }
    }
  }

  const handleAddToPrompt = (text: string) => {
    onChange(value ? `${value} ${text}` : text)
    inputRef.current?.focus()
  }

  // Handle adding a page as @ reference
  const handleAddPageReference = (page: PageInfo) => {
    if (atReferences.some((ref) => ref.id === page.id)) {
      toast.error("Page already referenced")
      return
    }

    const newRef: AtReference = {
      type: "page",
      id: page.id,
      name: page.name,
      content: page.html_content || undefined,
    }
    setAtReferences((prev) => [...prev, newRef])
    onChange(value ? `${value} @${page.name}` : `@${page.name}`)
    setShowAtDropdown(false)
    inputRef.current?.focus()
  }

  // Remove an @ reference
  const handleRemoveAtReference = (refId: string) => {
    const ref = atReferences.find((r) => r.id === refId)
    if (ref) {
      setAtReferences((prev) => prev.filter((r) => r.id !== refId))
      onChange(value.replace(new RegExp(`@${ref.name}\\s*`, "g"), "").trim())
    }
  }

  const defaultPlaceholder = isHero
    ? "Paste a design screenshot and watch it come to life..."
    : generationMode === "QUICKPATCH"
      ? "Quick edit: change text, colors, styles..."
      : generationMode === "EDIT"
        ? "Ask for revisions..."
        : "Describe what you want to create... (paste URL for style reference)"

  const showContextBadges = !isHero && (currentPageName || styleGuideSource)
  const showAtFeatures = pages.length > 0

  return (
    <>
      {/* Prompt Builder Modal */}
      <PromptBuilderModal
        isOpen={showPromptBuilder}
        onClose={() => setShowPromptBuilder(false)}
        onAddToPrompt={handleAddToPrompt}
      />

      <div className="w-full">
        {/* @ References Pills (compact variant only) */}
        {!isHero && atReferences.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {atReferences.map((ref) => (
              <span
                key={ref.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary text-xs rounded-full"
              >
                <FileText className="w-3 h-3" />
                <span>@{ref.name}</span>
                <button
                  onClick={() => handleRemoveAtReference(ref.id)}
                  className="ml-0.5 hover:bg-primary/30 rounded-full p-0.5"
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Input Card */}
        <div className={`bg-gray-900 ${containerClass} border border-gray-800`}>
          {/* Context Badges Row (compact variant only) */}
          {showContextBadges && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-800">
              {/* Current Page Badge */}
              {currentPageName && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium text-gray-300">
                  <FileText className="w-3 h-3" />
                  <span>{currentPageName}</span>
                </div>
              )}

              {/* Style Guide Badge */}
              {styleGuideSource && onStyleGuideChange && (
                <StyleGuideBadge
                  currentSource={styleGuideSource}
                  onSourceChange={onStyleGuideChange}
                  pages={pages}
                  savedStyleGuides={savedStyleGuides}
                  currentPageName={currentPageName}
                  onExtractFromCurrent={onExtractStyleGuide}
                  disabled={isLoading}
                />
              )}
            </div>
          )}

          {/* Textarea Area */}
          <div
            className={`relative ${isDragOver ? "ring-2 ring-primary ring-inset bg-primary/10" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Mode Toggle - Positioned top right (compact variant only) */}
            {!isHero && showModeToggle && onModeChange && (
              <div className="absolute top-2 right-2 z-10">
                <ModeToggle mode={generationMode} onModeChange={onModeChange} disabled={isLoading} />
              </div>
            )}

            {/* Style Reference Previews (URLs and Images) */}
            {(detectedUrls.length > 0 || uploadedImages.length > 0) && (
              <div className="flex flex-wrap gap-2 px-4 pt-3 pb-1">
                {/* URL chips */}
                {detectedUrls.map((url) => (
                  <div
                    key={url}
                    className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-500/20 border border-blue-500/30 rounded-md text-xs text-blue-300"
                  >
                    <Globe className="w-3 h-3" />
                    <span className="max-w-[150px] truncate">{url.replace(/^https?:\/\//, "")}</span>
                    <button
                      onClick={() => removeDetectedUrl(url)}
                      className="ml-1 hover:text-blue-100"
                      title="Remove URL reference"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {/* Image previews */}
                {uploadedImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative group inline-flex items-center gap-1.5 px-1.5 py-1 bg-purple-500/20 border border-purple-500/30 rounded-md"
                  >
                    <img src={img.preview} alt="Style reference" className="w-6 h-6 object-cover rounded" />
                    <span className="text-xs text-purple-300 max-w-[100px] truncate">{img.file.name}</span>
                    <button
                      onClick={() => removeUploadedImage(index)}
                      className="ml-1 text-purple-300 hover:text-purple-100"
                      title="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Drag-over overlay */}
            {isDragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-primary/20 z-20 pointer-events-none">
                <div className="text-primary text-sm font-medium">Drop image for style reference</div>
              </div>
            )}

            {/* Textarea */}
            <textarea
              ref={inputRef}
              value={value}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={placeholder || defaultPlaceholder}
              disabled={isLoading || isUploadingImage}
              className={textareaClass}
              rows={rows}
            />

            {/* @ Autocomplete Dropdown */}
            {showAtAutocomplete && filteredPages.length > 0 && (
              <div
                ref={autocompleteRef}
                className="absolute bottom-full left-4 mb-1 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
              >
                <div className="py-1">
                  {filteredPages.map((page, index) => (
                    <button
                      key={page.id}
                      onClick={() => handleAutocompleteSelect(page)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                        index === atAutocompleteIndex
                          ? "bg-primary/20 text-primary"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }`}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{page.name}</span>
                    </button>
                  ))}
                </div>
                <div className="px-3 py-1.5 border-t border-gray-700 text-xs text-gray-500">
                  ↑↓ to navigate • Enter to select • Esc to close
                </div>
              </div>
            )}
          </div>

          {/* Bottom Bar */}
          <div className={bottomBarClass}>
            <div className="flex items-center gap-1.5">
              {/* Prompt Builder */}
              <button
                onClick={() => setShowPromptBuilder(true)}
                disabled={isLoading}
                className={buttonClass}
                title="Prompt Builder"
              >
                <Wand2 className={isHero ? "w-4 h-4" : "w-3.5 h-3.5"} />
                {isHero ? "Prompt Builder" : <span className="hidden sm:inline">Prompt Builder</span>}
              </button>

              {/* Model Selector */}
              <ModelSelector
                selectedModelId={selectedModelId}
                onSelectModel={setSelectedModelId}
                isOpen={showModelDropdown}
                onOpenChange={setShowModelDropdown}
                compact={!isHero}
              />

              {/* @ Page References */}
              {showAtFeatures && (
                <div className="relative" ref={atDropdownRef}>
                  <button
                    onClick={() => setShowAtDropdown(!showAtDropdown)}
                    disabled={isLoading}
                    className={`${iconButtonClass} ${
                      atReferences.length > 0 ? "text-primary hover:bg-primary/20" : ""
                    }`}
                    title="Add page to context"
                  >
                    <AtSign className={isHero ? "w-4 h-4" : "w-3.5 h-3.5"} />
                  </button>

                  {/* @ Dropdown */}
                  {showAtDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-lg z-50">
                      <div className="p-2 border-b border-gray-700">
                        <div className="text-xs text-gray-400 font-medium">Add page to context</div>
                      </div>
                      <div className="py-1 max-h-48 overflow-y-auto">
                        {pages.length > 0 ? (
                          pages.map((page) => {
                            const isReferenced = atReferences.some((ref) => ref.id === page.id)
                            return (
                              <button
                                key={page.id}
                                onClick={() => handleAddPageReference(page)}
                                disabled={isReferenced}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                                  isReferenced
                                    ? "bg-primary/10 text-primary cursor-not-allowed"
                                    : "text-gray-300 hover:bg-gray-800 hover:text-white"
                                }`}
                              >
                                <FileText className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{page.name}</span>
                                {isReferenced && <span className="text-xs text-primary ml-auto">Added</span>}
                              </button>
                            )
                          })
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500">No pages available</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Attachments */}
              <button
                onClick={handleFilePickerClick}
                disabled={isLoading || isUploadingImage}
                className={iconButtonClass}
                title="Attach Style Reference Image"
              >
                <Paperclip className={isHero ? "w-4 h-4" : "w-3.5 h-3.5"} />
              </button>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleFileInputChange}
                className="hidden"
                multiple
              />

              {/* Code Snippets */}
              <button disabled={isLoading} className={iconButtonClass} title="Code Snippets">
                <Braces className={isHero ? "w-4 h-4" : "w-3.5 h-3.5"} />
              </button>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!value.trim() || isLoading}
              className="p-2.5 bg-white text-gray-900 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Hint */}
        <p className="text-center text-sm text-gray-500 mt-3">
          {isHero ? (
            "Press ⌘ + Enter to generate"
          ) : (
            <>
              Press Enter to send •{" "}
              {generationMode === "QUICKPATCH"
                ? "Quick mode: fast JSON patches (~90% fewer tokens)"
                : generationMode === "EDIT"
                  ? "Edit mode: preserves structure"
                  : "Create mode: full generation"}
            </>
          )}
        </p>
      </div>
    </>
  )
}

export default AuraPromptInput
